// Libraries
import React from 'react'
import moment from 'moment'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'

// Components
import BookExperienceLink from '../Workflows/Reservation/BookExperienceLink'
import HostReviewLink from '../Workflows/HostReview/HostReviewLink'
// import ConvertCurrency from '../../utils/convertCurrency'

// New component for the quantity selector
const SeatQuantitySelector = ({ selectedSeats, maxSeats, handleSeatAmount }) => {
  // If maxSeats is 0 (no schedule selected or sold out), display a disabled message.
  if (maxSeats === 0) {
    return (
      <select disabled className="form-control" style={{ pointerEvents: 'none' }}>
        <option value={0}>Select your seats</option>
      </select>
    )
  }

  // Ensure selectedSeats is at least 1 when a schedule is selected, or 0 if it was manually deselected
  const currentSeats = selectedSeats || 1

  const decrementSeats = () => {
    if (currentSeats > 1) {
      // Mimic the event structure expected by handleSeatAmount
      handleSeatAmount({ target: { value: currentSeats - 1 } })
    } else {
      // Allow going to 0 (unselected)
      handleSeatAmount({ target: { value: 0 } })
    }
  }

  const incrementSeats = () => {
    if (currentSeats < maxSeats) {
      // Mimic the event structure expected by handleSeatAmount
      handleSeatAmount({ target: { value: currentSeats + 1 } })
    }
  }

  // Base Style
  const buttonBaseStyle = { 
    width: '80px', 
    height: '100%', 
    border: 'none', 
    outline: 'none', 
    background: '#111111', 
    color: '#ffffff', 
    fontSize: '1.5rem', 
    fontWeight: 'bold', 
    cursor: 'pointer',
    transition: 'background 0.3s' // Added transition for a smoother effect
  }

  // Disabled Style - Applied conditionally via prop
  const buttonDisabledStyle = {
    background: '#cccccc', // Lighter grey background for disabled state
    color: '#999999',     // Darker grey text for disabled state
    cursor: 'not-allowed' // Change cursor on disabled
  }

  // Function to determine the style for the decrement button
  const getDecrementButtonStyle = () => {
    if (currentSeats <= 1) {
      return { ...buttonBaseStyle, ...buttonDisabledStyle };
    }
    return buttonBaseStyle;
  }

  // Function to determine the style for the increment button
  const getIncrementButtonStyle = () => {
    if (currentSeats >= maxSeats) {
      return { ...buttonBaseStyle, ...buttonDisabledStyle };
    }
    return buttonBaseStyle;
  }

  return (
    <div className="input-group seat-quantity-selector" style={{ display: 'flex', borderRadius:'40px', overflow:'hidden', background: "#ffffff", height: '40px', borderRadius: '40px' }}>
      <div className="input-group-prepend">
        <button
          type="button"
          className=""
          onClick={decrementSeats}
          disabled={currentSeats <= 1}
          style={getDecrementButtonStyle()}
        >
          -
        </button>
      </div>
      <input
        type="text"
        className="form-control text-center"
        value={currentSeats + (currentSeats === 1 ? ' Seat' : ' Seats')}
        readOnly
        style={{ flex: 1, textAlign: 'center',height: '100%', border: 'none', boxShadow: 'none', outline: 'none', background: 'transparent', color: '#111111' }}
      />
      <div className="input-group-append">
        <button
          type="button"
          className=""
          onClick={incrementSeats}
          disabled={currentSeats >= maxSeats}
          style={getIncrementButtonStyle()}
        >
          +
        </button>
      </div>
    </div>
  )
}

// --- SeatRequest Component ---

export default function SeatRequest({
  experience,
  handleSeatAmount,
  handleSchedule,
  // seatSelectOptions, // REMOVED
  maxSeats, // ADDED
  scheduleSelectOptions,
  reservationRequest,
  currentUser,
  setPaymentModel,
  paymentModal,
  selectedSchedule,
  selectedSeats, // No default here, rely on parent prop
  showBookAseat,
  showBookAseatForSoldOut,
  reservationActions,
  dateError
}) {
  const cost = `${experience.currency} ${experience.price}`

  let inPast = false

  if (experience.dateWithTime) {
    if (moment(experience.dateWithTime).isBefore(moment(new Date()).utc())) {
      inPast = true
    }
  } else {
    inPast = true
  }
  // eslint-disable-next-line no-console
  // console.log('showBookAseatForSoldOut', showBookAseatForSoldOut, 'showBookAseat', showBookAseat, inPast, moment(new Date()).utc(), moment(experience.date).format('YYYY-MM-DD') + 'T' + experience.time + 'Z')

  let cardTitle = 'Request a Booking'

  if (experience.instant) {
    cardTitle = 'Make a Booking'
  }
  if (experience.status !== 'approved') {
    cardTitle = 'Experience Details'
  }

  if (experience.hasPassed) {
    cardTitle = 'Experience has ended'
  } else if (experience.status && experience.status === 'paused') {
    cardTitle = 'Experience is Paused'
  } else if (scheduleSelectOptions() && scheduleSelectOptions().length === 0) {
    cardTitle = 'Experience Sold Out'
  }

  const matchingSelectedSchedule = experience.schedules.find((s) => s.id === selectedSchedule)
  const flagFalse = false
  const seatsForBooking = selectedSeats || 1; 

  // Helper function to handle policy text logic
  const renderPolicyMessage = () => {
    const noRefundIds = [1368, 1375, 1377, 1378, 1379];
    const rescheduleOnlyIds = [1248];
    const excludedIds = [549]; // IDs that explicitly don't show the standard 48hr message

    const approvalMsg = !experience.instant ? 'Reservations are subject to approval from the host.' : null;
    let policyMsg = null;

    if (noRefundIds.includes(experience.id)) {
      policyMsg = "Please note that once you have booked, there will be no exchange or refund for this event.";
    } else if (rescheduleOnlyIds.includes(experience.id)) {
      policyMsg = "Reservations can only be rescheduled to future events but won't be refunded if cancelled.";
    } else if (excludedIds.includes(experience.id)) {
      // Logic from original code: (id !== 549) implies 549 gets no standard message
      policyMsg = null;
    } else {
      // Default standard policy
      policyMsg = "Reservations can be cancelled with full refund up to 48 hours before the start of the experience.";
    }

    return (
      <React.Fragment>
        {approvalMsg}
        {approvalMsg && policyMsg && <br />}
        {policyMsg}
      </React.Fragment>
    );
  };
  /*
  Case 1 - Schedule is yet to be created (schedule array is empty)
  Case 2 - Schedule is active and bookings can be made
  Case 3 - Schedule is active and But all seats are sold out
  Case 4 - Schedule is active but Bookings Closed (24 hour time)
  Case 5 - Schedule is paused - schedule status is paused or experience status is paused
  Case 6 - Schedule is over - Past Event
  */
  return (
    <aside className="experience-single__seat-requests make-a-booking-opacity">
      <div className="experience-single__seat-request--header">
        <h4>{cardTitle}</h4>
      </div>
      <div className="experience-single__seat-request--content" style={{ textAlign: 'left', padding: '25px' }}>
        {experience.status === 'approved' && (
          <React.Fragment>
            <ul className="list-unstyled" style={{ marginBottom: '20px' }}>
              <li>
                <label className='booking-seat-label'>Time</label>
                <p>{moment(experience.time, 'HH:mm').format('h:mm A')}</p>
              </li>
              <li>
                <label className='booking-seat-label'>Duration</label>
                <p>{experience.duration.toString().replace('.0', '')} hours</p>
              </li>
              {/* <li>
                <label>Time</label>
                {experience.time}
              </li> */}
              {/* <li>
                <label>Start Time</label>
                {moment(experience.start_time, 'HH:mm').format('h:mm A')}
              </li>
              <li>
                <label>End Time</label>
                {moment(experience.end_time, 'HH:mm').format('h:mm A')}
              </li> */}
              <li>
                <label className='booking-seat-label'>Location</label>
                <p>
                  {experience.location.neighbourhood}, {experience.location.city}
                </p>
              </li>
              <li>
                <label className='booking-seat-label'>Cost</label>
                <p>{cost}</p>
              </li>
              {/* {false && (
                <React.Fragment>
                  <li>
                    <label className='booking-seat-label'>Total Seats</label>
                    <p>{experience.seats}</p>
                  </li>
                  <li>
                    <label className='booking-seat-label'>Seats Available</label>
                    <p>{experience.seatsAvailable < 0 ? 0 : experience.seatsAvailable}</p>
                  </li>
                </React.Fragment>
              )} */}
            </ul>
            {/* {scheduleSelectOptions() && scheduleSelectOptions().length > 1 ? ( */}
            <p className="form-note" style={{ margin: '20px 0px' }}>
              {/* {!experience.instant && 'Reservations are subject to approval from the host.'}
              {!experience.instant && <br />}
              {(experience.id !== 549 && experience.id!==1248 && experience.id!==1368) && 'Reservations can be cancelled with full refund up to 48 hours before the start of the experience.'}
              {experience.id ==1248 && "Reservations can only be rescheduled to future events but won't be refunded if cancelled."}
              {experience.id ==1368 && "Please note that once you have booked, there will be no exchange or refund for this event."} */}
              {renderPolicyMessage()}
            </p>
            {/* ) : (<p>New Schedule Coming Soon</p>)} */}

            {flagFalse && currentUser.attributes.id === experience.host.id ? (
              <div className="experience-single__seat-request--content__btn-group ">
                <Link to={`/hosts/reservations/${experience.id}`}>
                  <button type="button" className="btn btn--full mb-5" style={{ minWidth: '240px' }}>
                    View Bookings{' '}
                  </button>
                </Link>

                {/* {experience.reviewsPending ? ( */}
                <HostReviewLink className="btn btn--full " experience={experience}>
                  Complete Guest Reviews
                </HostReviewLink>
                {/* ) : (
                  ''
                )} */}
              </div>
            ) : (
              <React.Fragment>
                {inPast ? (
                  <div>
                    <p className="text-success" style={{ color: '#fff', fontWeight: 700, fontSize: '1.5rem', textAlign: 'center' }}>
                      New Schedule Coming Soon
                    </p>
                  </div>
                ) : (
                  <div className="experience-single__seat-request--content__btn-group">
                    {showBookAseatForSoldOut ? (
                      <div>
                        {experience.status !== 'paused' && showBookAseat ? (
                          <React.Fragment>
                            {scheduleSelectOptions() && scheduleSelectOptions().length > 1 && (
                              <p>
                                <select className='' value={reservationRequest.schedules} onChange={handleSchedule} style={{'height':'40px', 'display':'flex', justifyContent:'center', alignItems:'center', borderRadius:'40px', paddingLeft:'15px', paddingRight:'15px','color':'#111111'}}>
                                  {scheduleSelectOptions()}
                                </select>
                                {dateError ? <span style={{ color: 'red' }}>{dateError}</span> : ''}
                              </p>
                            )}
                            {/* {(matchingSelectedSchedule && experience.id !== 549) && (
                              <React.Fragment>
                                <ul className="list-unstyled" style={{ marginBottom: '10px' }}>
                                  <li style={{ marginBottom: '11.1px' }}>
                                    <label className='booking-seat-label'>Seats Available</label>
                                    <p>
                                      {matchingSelectedSchedule.seatsAvailable <= 0 ? 0 : matchingSelectedSchedule.seatsAvailable} / {matchingSelectedSchedule.seats}
                                    </p>
                                  </li>
                                </ul>
                              </React.Fragment>
                            )} */}
                            {selectedSchedule && matchingSelectedSchedule && matchingSelectedSchedule.seatsAvailable > 0 ? (
                              <React.Fragment>
                                {selectedSchedule && (
                                  <p>
                                    {/* REPLACED THE OLD <select> WITH THE NEW QUANTITY SELECTOR */}
                                    <SeatQuantitySelector
                                      selectedSeats={selectedSeats}
                                      maxSeats={maxSeats}
                                      handleSeatAmount={handleSeatAmount}
                                    />
                                  </p>
                                )}
                                <p>
                                  <BookExperienceLink
                                    reservationActions={reservationActions}
                                    setPaymentModel={setPaymentModel}
                                    paymentModal={paymentModal}
                                    className="btn btn--full book-a-seat"
                                    // 💡 USE seatsForBooking (which is now 1 by default from parent state)
                                    selectedSeats={seatsForBooking}
                                    selectedSchedule={selectedSchedule}
                                  >
                                    {experience.instant ? 'Book Now' : 'Request Booking'}
                                  </BookExperienceLink>
                                </p>
                              </React.Fragment>
                            ) : (
                              <React.Fragment>
                                {!selectedSchedule ? (
                                  <p>
                                    <BookExperienceLink
                                      reservationActions={reservationActions}
                                      setPaymentModel={setPaymentModel}
                                      paymentModal={paymentModal}
                                      className="btn btn--full book-a-seat"
                                      // 💡 USE seatsForBooking
                                      selectedSeats={seatsForBooking}
                                      selectedSchedule={selectedSchedule}
                                    >
                                      {experience.instant ? 'Book Now' : 'Request Booking'}
                                    </BookExperienceLink>
                                  </p>
                                ) : (
                                  <p className="text-success" style={{ color: '#000000', fontWeight: 700, fontSize: '2rem', textAlign: 'center' }}>
                                    Sold Out
                                  </p>
                                )}
                              </React.Fragment>
                            )}
                          </React.Fragment>
                        ) : (
                          <div>
                            <p style={{ color: '#000000', fontWeight: 700, fontSize: '2rem', textAlign: 'center' }}>Paused</p>
                            <p>
                              <button type="button" className="btn btn--full mb-5" style={{ minWidth: '240px' }} disabled>
                                Book a Seat
                              </button>
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <p className="text-success" style={{ color: '#000000', fontWeight: 700, fontSize: '2rem', textAlign: 'center' }}>
                          Sold Out
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        )}
        {experience.status !== 'approved' && (
          <React.Fragment>
            <p className="text-success" style={{ color: '#000000', fontWeight: 700, fontSize: '1.5rem', textAlign: 'center' }}>
              Coming Soon
            </p>
          </React.Fragment>
        )}
      </div>
    </aside>
  )
}

// --- Prop Definitions ---

SeatRequest.defaultProps = {
  selectedSchedule: null,
  selectedSeats: null, 
  showBookAseat: true,
  showBookAseatForSoldOut: false,
  maxSeats: 0 
}

SeatRequest.propTypes = {
  experience: PropTypes.shape({
    hasPassed: PropTypes.bool,
    paused: PropTypes.string,
    seatsAvailable: PropTypes.number || PropTypes.string,
    date: PropTypes.instanceOf(Date),
    dateWithTime: PropTypes.instanceOf(Date),
    price: PropTypes.number,
    currency: PropTypes.string,
    duration: PropTypes.string,
    time: PropTypes.string,
    status: PropTypes.string,
    reviewsPending: PropTypes.bool,
    hasBooked: PropTypes.bool,
    host: PropTypes.shape({
      id: PropTypes.number
    }),
    schedules: PropTypes.arrayOf(
      PropTypes.shape({
        status: PropTypes.string,
        seatsAvailable: PropTypes.number || PropTypes.string
      })
    ),
    seats: PropTypes.number,
    location: PropTypes.shape({
      city: PropTypes.string,
      countryCode: PropTypes.string,
      neighbourhood: PropTypes.string
    }),
    id: PropTypes.number,
    instant: PropTypes.bool
  }).isRequired,
  handleSeatAmount: PropTypes.func.isRequired,
  handleSchedule: PropTypes.func.isRequired,
  // seatSelectOptions: PropTypes.func.isRequired, // REMOVED
  maxSeats: PropTypes.number.isRequired, // ADDED
  scheduleSelectOptions: PropTypes.func.isRequired,
  reservationRequest: PropTypes.shape({
    schedules: PropTypes.number,
    seats: PropTypes.number
  }).isRequired,
  setPaymentModel: PropTypes.func.isRequired,
  paymentModal: PropTypes.bool.isRequired,
  currentUser: PropTypes.shape({
    attributes: PropTypes.shape({
      id: PropTypes.number
    })
  }).isRequired,
  selectedSchedule: PropTypes.number,
  selectedSeats: PropTypes.number,
  showBookAseat: PropTypes.bool,
  showBookAseatForSoldOut: PropTypes.bool,
  dateError: PropTypes.string
}